/**
 * The Selection box: the area range's beats (SRVR-0322 AC4) and the desktop editor's Selection
 * Options toggles (SRVR-0318), which decide which kinds a range delete, copy, cut or flip touches.
 */
import React, { useEffect, useRef } from 'react';
import { useEditorStore } from '../../store/editorStore';
import { NOTE_FILTER_KINDS, WALL_FILTER_KINDS, EVENT_FILTER_KINDS, PASTE_MODES, effectiveAreaRange, formatBeat, type AreaFilterKind, type AreaRange, type PasteMode } from '../../store/areaSelection';
import {
  readoutStyle, readoutEmptyStyle, groupLabelStyle, toggleGridStyle, toggleRowStyle, masterRowStyle, checkboxStyle,
} from './AreaSelectionBox.styles';

// Distinct from the Obstacles panel's button labels, so a toggle is never read as a placement button.
const LABEL: Record<AreaFilterKind, string> = {
  right_hand: 'Right hand', left_hand: 'Left hand', one_hand_special: 'One hand', both_hands_special: 'Both hands',
  wall_right: 'Right wall', wall_left: 'Left wall', center: 'Center wall', angle_right: 'Diag R wall', angle_left: 'Diag L wall',
  crouch: 'Crouch wall', square: 'Square wall', triangle: 'Triangle wall',
  lights: 'Lights', effects: 'Effects',
};

const ALL_KINDS = [...NOTE_FILTER_KINDS, ...WALL_FILTER_KINDS, ...EVENT_FILTER_KINDS] as const;

/** The desktop editor's Paste Behavior radio group, in its Shift+A cycle order. */
const PASTE_LABEL: Record<PasteMode, string> = {
  deleteExisting: 'Delete existing', prioritizeExisting: 'Prioritize existing', prioritizePasted: 'Prioritize pasted',
};

/** The desktop editor's banner: "<start> to <end>", each a whole beat and a fraction (`formatBeat`). */
const readout = (r: AreaRange | null) => (r ? `Beats ${formatBeat(r.startBeat)} to ${formatBeat(r.endBeat)}` : 'No range');

function Toggle({ label, checked, onChange, master, testId }: {
  label: string; checked: boolean; onChange: (on: boolean) => void; master?: boolean; testId: string;
}) {
  return (
    <label style={master ? masterRowStyle : toggleRowStyle}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} style={checkboxStyle} data-testid={testId} />
      {label}
    </label>
  );
}

export default function AreaSelectionBox() {
  const range = useEditorStore(s => s.areaRange);
  const filters = useEditorStore(s => s.areaFilters);
  const setAreaFilters = useEditorStore(s => s.setAreaFilters);
  const pasteMode = useEditorStore(s => s.pasteMode);
  const setPasteMode = useEditorStore(s => s.setPasteMode);
  const allOn = (kinds: readonly AreaFilterKind[]) => kinds.every(k => filters[k]);
  const readoutRef = useRef<HTMLDivElement>(null);

  // The readout's text is written only through the ref, never rendered by React, so the two cannot
  // fight over the text node. The committed range is written when it changes; while Z is held the
  // range follows the playhead and so does the readout, from a store subscription rather than a
  // selector, because a component must never re-render on the playhead (style: frontend 7).
  useEffect(() => { if (readoutRef.current) readoutRef.current.textContent = readout(range); }, [range]);
  useEffect(() => useEditorStore.subscribe(s => {
    const el = readoutRef.current;
    if (!el || !s.areaGesture) return;
    const text = readout(effectiveAreaRange(s.areaRange, s.areaGesture, s.playback.currentBeat));
    if (el.textContent !== text) el.textContent = text;
  }), []);

  return (
    <div data-testid="area-selection-box">
      <div ref={readoutRef} style={range ? readoutStyle : readoutEmptyStyle} data-testid="area-range-readout" />
      <div style={toggleGridStyle}>
        <Toggle master label="All" checked={allOn(ALL_KINDS)} onChange={on => setAreaFilters(ALL_KINDS, on)} testId="area-filter-all" />
        <span />
        <Toggle master label="All notes" checked={allOn(NOTE_FILTER_KINDS)} onChange={on => setAreaFilters(NOTE_FILTER_KINDS, on)} testId="area-filter-all-notes" />
        <Toggle master label="All walls" checked={allOn(WALL_FILTER_KINDS)} onChange={on => setAreaFilters(WALL_FILTER_KINDS, on)} testId="area-filter-all-walls" />
      </div>
      <div style={groupLabelStyle}>Notes</div>
      <div style={toggleGridStyle}>
        {NOTE_FILTER_KINDS.map(k => (
          <Toggle key={k} label={LABEL[k]} checked={filters[k]} onChange={on => setAreaFilters([k], on)} testId={`area-filter-${k}`} />
        ))}
      </div>
      <div style={groupLabelStyle}>Walls</div>
      <div style={toggleGridStyle}>
        {WALL_FILTER_KINDS.map(k => (
          <Toggle key={k} label={LABEL[k]} checked={filters[k]} onChange={on => setAreaFilters([k], on)} testId={`area-filter-${k}`} />
        ))}
      </div>
      {/* Unity's Event Types: Lights and Effects (SRVR-0318 AC8). */}
      <div style={groupLabelStyle}>Events</div>
      <div style={toggleGridStyle}>
        {EVENT_FILTER_KINDS.map(k => (
          <Toggle key={k} label={LABEL[k]} checked={filters[k]} onChange={on => setAreaFilters([k], on)} testId={`area-filter-${k}`} />
        ))}
      </div>
      <div style={groupLabelStyle}>Paste behavior</div>
      <div role="radiogroup" aria-label="Paste behavior">
        {PASTE_MODES.map(m => (
          <label key={m} style={toggleRowStyle}>
            <input type="radio" name="area-paste-mode" checked={pasteMode === m} onChange={() => setPasteMode(m)}
              style={checkboxStyle} data-testid={`area-paste-${m}`} />
            {PASTE_LABEL[m]}
          </label>
        ))}
      </div>
    </div>
  );
}
